import { useEffect, useState } from "react";
import { format, startOfDay } from "date-fns";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InputDatePicker } from "@/components/ui/input-date-picker";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import LoadingSpinner from "@/components/LoadingSpinner";

type TemplatePlanSummary = {
  id: string;
  name: string;
  org_id: string | null;
  is_template: boolean;
  scheduling_mode?: "fixed" | "dated" | null;
};

type TrainingTemplateLinkDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templatePlans: TemplatePlanSummary[];
  linkedPlanIds: Set<string>;
  onLinkTemplate: (
    template: TemplatePlanSummary,
    startDate?: string,
  ) => Promise<boolean>;
};

export function TrainingTemplateLinkDialog({
  open,
  onOpenChange,
  templatePlans,
  linkedPlanIds,
  onLinkTemplate,
}: TrainingTemplateLinkDialogProps) {
  const weekStartsOn = useWeekStartsOn();
  const [step, setStep] = useState<"select" | "start">("select");
  const [pendingTemplate, setPendingTemplate] =
    useState<TemplatePlanSummary | null>(null);
  const [startDate, setStartDate] = useState(
    format(startOfDay(new Date()), "yyyy-MM-dd"),
  );
  const [linking, setLinking] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep("select");
      setPendingTemplate(null);
      setStartDate(format(startOfDay(new Date()), "yyyy-MM-dd"));
      setLinking(false);
    }
  }, [open]);

  const closeDialog = (nextOpen: boolean) => {
    onOpenChange(nextOpen);
  };

  const handleDirectLink = async (template: TemplatePlanSummary) => {
    setLinking(true);
    const success = await onLinkTemplate(template);
    setLinking(false);
    if (success) {
      onOpenChange(false);
    }
  };

  const handleConfirmLinkWithStart = async () => {
    if (!pendingTemplate) return;
    setLinking(true);
    const success = await onLinkTemplate(pendingTemplate, startDate);
    setLinking(false);
    if (success) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={closeDialog}
      analyticsName="training_template_link_dialog"
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {step === "start"
              ? "Choose template start day"
              : "Choose a training template"}
          </DialogTitle>
          <DialogDescription>
            {step === "start"
              ? "Set the day this calendar template should start for this client."
              : "Link a template plan to this client. Any edits will create a new plan on save."}
          </DialogDescription>
        </DialogHeader>
        <div className="relative overflow-hidden">
          <div
            className="flex w-[200%] transition-transform duration-300 ease-in-out"
            style={{
              transform: step === "start" ? "translateX(-50%)" : "translateX(0%)",
            }}
          >
            <div className="w-1/2 space-y-2 pr-1">
              {templatePlans.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No training templates found.
                </p>
              ) : (
                templatePlans.map((template) => {
                  const isLinked = linkedPlanIds.has(template.id);
                  const isCalendarTemplate = template.scheduling_mode === "dated";
                  return (
                    <div
                      key={template.id}
                      className="flex items-center justify-between rounded-md border px-3 py-2"
                    >
                      <div className="flex flex-col">
                        <span className="text-sm font-medium">{template.name}</span>
                        {isCalendarTemplate ? (
                          <span className="text-xs text-muted-foreground">
                            Calendar template
                          </span>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={isLinked || linking}
                        onClick={() => {
                          if (isCalendarTemplate) {
                            setPendingTemplate(template);
                            setStartDate(format(startOfDay(new Date()), "yyyy-MM-dd"));
                            setStep("start");
                            return;
                          }
                          void handleDirectLink(template);
                        }}
                      >
                        {isLinked ? "Linked" : "Link"}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
            <div className="w-1/2 pl-1">
              <div className="space-y-3 rounded-md border p-3">
                <div className="text-sm font-medium">
                  {pendingTemplate?.name ?? "Template"}
                </div>
                <div className="space-y-1">
                  <InputDatePicker
                    label="Start day"
                    value={startDate}
                    onChange={setStartDate}
                    weekStartsOn={weekStartsOn}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setStep("select");
                      setPendingTemplate(null);
                    }}
                    disabled={linking}
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => void handleConfirmLinkWithStart()}
                    disabled={!pendingTemplate || linking}
                  >
                    Link template
                    {linking ? <LoadingSpinner className="ml-2" /> : null}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
